import React from 'react';
//import PropTypes from 'prop-types';

import { createUseStyles } from '../palette';
import * as search from '../common/utils/search';
import decorate from '../common/utils/decorate';
import AddressInput from '../common/components/AddressInput';
import Map from '../common/components/Map';
import DeliveryWidgetContext from '../contexts/DeliveryWidgetContext';
import Button from '../ui/Button';
import ButtonGroup from '../ui/ButtonGroup';
import TextInput from '../ui/TextInput';
import Tooltip from '../ui/Tooltip';
import Loading from '../ui/Loading';
import Card from '../ui/Card';
import Checkbox from '../ui/Checkbox';
import Grid from '../ui/Grid';
import Skeleton from '../ui/Skeleton';
import Text from '../ui/Text';
import Logo from '../ui/Logo';
import Icon from '../ui/Icon';

import Query from './Query';

const SEARCH_FIELDS = {
  'point.title': { highlight: true },
  'point.address': { highlight: true },
  'point.metro.*.title': { highlight: true }
};

const useStyles = createUseStyles((theme) => ({
  mapContainer: {
    borderStyle: theme.get('card', 'border.style'),
    borderWidth: theme.get('card', 'border.width'),
    borderColor: theme.getColor('card', 'basic', 'border'),
    marginBottom: 10,

    position: 'relative',
    height: 320
  },

  pointInfo: {
    margin: -1,
    padding: theme.getFactor('card', 'indent', 0.5),

    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -10,
    maxHeight: '60%',

    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      padding: theme.getFactor('card', 'indent', 0.5),
      gap: theme.getFactor('card', 'indent', 0.5),

      height: '100%'
    }
  },

  pointCard: {
    '& svg': {
      marginBottom: 2
    }
  },

  loadingBackdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',

    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',

    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%'
  },

  highlight: {
    backgroundColor: theme.getColor('card', 'basic', 'contrast'),
    color: theme.getColor('card', 'basic', 'value'),

    margin: 0,
    padding: 0
  }
}));

export default decorate(
  class extends React.PureComponent {
    static contextType = DeliveryWidgetContext;

    static decorateWith = {
      classes: useStyles
    };

    static propTypes = {};

    static defaultProps = {};

    constructor(props) {
      super(props);

      this.state = {
        display: 'list',
        points: undefined,
        stack: null,
        search: ''
      };
    }

    renderMapStack = (stack, selected) => {
      //const { onSelectPoint } = this.props;

      return (
        <Map
          type="marker"
          key={stack[0].id}
          position={stack[0].location}
          icon={{
            url: selected
              ? '/data/images/global/delivery/marker-sel.png'
              : '/data/images/global/delivery/marker.png',
            anchorX: 16,
            anchorY: 32,
            width: 32,
            height: 32
          }}
          onClick={this.mapStackClick(stack)}
          onMouseEnter={this.showMapStackTooltip(stack)}
          onMouseLeave={this.hideMapStackTooltip}
        />
      );
    };

    stackPointClick = (stack = null, point = null) => {
      const { selectedPoint, onSelectPoint } = this.props;

      if (point || !stack) {
        this.setState((state) => ({
          ...state,
          stack,
          mapCenter: stack?.[0].location || null
        }));

        if (typeof onSelectPoint === 'function') onSelectPoint(point);
      } else if (
        this.state.stack === stack &&
        (!selectedPoint || stack.length === 1)
      ) {
        this.setState((state) => ({ ...state, stack: null, mapCenter: null }));

        if (typeof onSelectPoint === 'function') onSelectPoint(null);
      } else {
        this.setState((state) => ({
          ...state,
          stack,
          mapCenter: stack[0].location
        }));

        if (typeof onSelectPoint === 'function')
          onSelectPoint(stack.length === 1 ? stack[0] : null);
      }
    };

    mapStackClick =
      (stack = null, point = null) =>
      (event) =>
        this.stackPointClick(stack, point);

    showMapStackTooltip = (stack) => (event) => {
      const {
        langString,

        company
      } = this.context;

      const mapRect = this.mapContainer?.getBoundingClientRect() || {
        left: 0,
        top: 0
      };

      this.mapStackTooltipTimeout = setTimeout(() => {
        if (!this.mapStackTooltipData) return;

        const point = stack.length ? stack[0] : null;

        this.mapStackTooltipData.showTooltip(
          !stack ? (
            ' '
          ) : stack.length > 1 ? (
            langString('options').replace('%', stack.length)
          ) : (
            <>
              <Grid container>
                <Grid size="sm">
                  {company.logo_path ? (
                    <Logo src={company.logo_path} />
                  ) : (
                    company.title
                  )}
                </Grid>
                <Grid grow={0}>
                  <Text strong>{this.pickupTypeTitle(point.type)}</Text>
                </Grid>
              </Grid>

              <Text size="sm">
                <Icon name="location" />

                {point.address}
              </Text>

              {point.description ? (
                <Text size="sm">
                  <Icon name="notepadEdit" />

                  {point.description}
                </Text>
              ) : null}

              {Array.isArray(point.schedule) &&
              point.schedule.find((item) => item) ? (
                <Text size="sm">
                  <Icon name="time" />

                  {this.scheduleToString(point.schedule)}
                </Text>
              ) : null}

              {/*<Text size="sm">
                <Icon name="boxSize" />
                
                111
              </Text>*/}
            </>
          ),
          (this.mousePosition?.[0] || 0) - mapRect.left,
          (this.mousePosition?.[1] || 0) - mapRect.top
        );
      }, 500);
    };

    hideMapStackTooltip = (event) => {
      if (this.mapStackTooltipTimeout) {
        clearTimeout(this.mapStackTooltipTimeout);

        this.mapStackTooltipTimeout = null;
      }

      if (this.mapStackTooltipData) this.mapStackTooltipData.hideTooltip();
    };

    onMouseMove = (event) => {
      this.mousePosition = [event.clientX, event.clientY];
    };

    pickupTypeTitle = (type) => {
      const { langString } = this.context;

      return type === 1 ? (
        <Text whiteSpace="nowrap">{langString('point')}</Text>
      ) : type === 2 ? (
        <Text whiteSpace="nowrap">{langString('parcellocker')}</Text>
      ) : type === 3 ? (
        <Text whiteSpace="nowrap">{langString('post')}</Text>
      ) : type === 4 ? (
        <Text whiteSpace="nowrap">{langString('terminal')}</Text>
      ) : (
        <Text whiteSpace="nowrap">{langString('office')}</Text>
      );
    };

    scheduleToString = (schedule) => {
      const { langString } = this.context;

      const weekDays = langString('week.days').split(',');

      const result = [];

      let lastValue = null;
      let lastIndex = 0;

      weekDays.forEach((day, index) => {
        const item = schedule[index];
        const value =
          Array.isArray(item) && item[0] && item[1]
            ? item[0] + ' - ' + item[1]
            : null;

        const isLast = index === weekDays.length - 1;

        if (lastValue !== value || isLast) {
          if (lastValue) {
            if (index - lastIndex > 1) result.push('-', weekDays[index - 1]);

            result.push(' ', lastValue);

            if (!isLast) result.push(', ');
          }

          if (value) result.push(day);

          lastValue = value;
          lastIndex = index;
        }
      });

      return result.join('');
    };

    componentDidMount() {
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mousedown', this.hideMapStackTooltip);
    }

    componentWillUnmount() {
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mousedown', this.hideMapStackTooltip);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {}

    render() {
      const {
        classes,

        selectedPoint,

        onNextPage,
        onPrevPage
      } = this.props;

      const {
        lang,
        shop,
        site,
        currency,
        cart,
        warehouse,
        editor,

        localityData,

        langString,
        priceFormat,
        daysFormat,
        apiRequest,

        service,
        method,
        company,
        tariff
      } = this.context;

      if (this.state.points === undefined) {
        this.state.points = null;

        const promise = apiRequest('delivery/points/list', {
          shop,
          site,
          currency,
          service: service.code,
          locality: AddressInput.buildValue(
            'delivery',
            'locality',
            localityData
          ),
          method: method.id,
          company: company.id,
          tariff: tariff.id,
          cart: cart.list,
          warehouse: warehouse.id
        });

        promise
          .then((data) => {
            const points = [];

            if (data.list) {
              data.list.forEach((pointItem) => {
                let stack = points.find(
                  (list) =>
                    Math.abs(list[0].location[0] - pointItem.location[0]) <
                      0.00001 &&
                    Math.abs(list[0].location[1] - pointItem.location[1]) <
                      0.00001
                );

                if (!stack) points.push((stack = []));

                if (editor && !selectedPoint && pointItem.id === editor.point) {
                  setTimeout(() => this.stackPointClick(stack, pointItem), 1);
                } else if (
                  !selectedStack &&
                  pointItem.id === selectedPoint?.id
                ) {
                  setTimeout(() => this.stackPointClick(stack, pointItem), 1);
                }

                stack.push(pointItem);
              });
            }

            this.setState((state) => ({ ...state, points }));
          })
          .catch((error) => {
            console.error(error);

            this.setState((state) => ({ ...state, points: [] }));
          });
      }

      let pointsList = null;

      if (this.state.display === 'list') {
        pointsList = this.state.points?.flatMap((stack) =>
          stack.map((point) => ({ value: point.id, stack, point }))
        );

        if (this.state.search)
          pointsList = search.filterList(
            SEARCH_FIELDS,
            pointsList,
            this.state.search,
            { className: classes.highlight }
          );
      }

      const selectedStack = this.state.stack;

      return (
        <Query
          cart={cart}
          rightChildren={
            <Button
              variant="contained"
              disabled={!selectedPoint || !this.state.points}
              onClick={onNextPage}
            >
              {langString('point.select')}
            </Button>
          }
        >
          <Grid container>
            <Grid>
              <Text strong size="xl">
                {langString('point.choose')}
              </Text>

              <div>
                <ButtonGroup>
                  <Button
                    variant={
                      this.state.display === 'list' ? 'contained' : 'text'
                    }
                    onClick={(event) =>
                      this.setState((state) => ({ ...state, display: 'list' }))
                    }
                  >
                    {langString('point.list')}
                  </Button>

                  <Button
                    variant={
                      this.state.display === 'map' ? 'contained' : 'text'
                    }
                    onClick={(event) =>
                      this.setState((state) => ({ ...state, display: 'map' }))
                    }
                  >
                    {langString('map.show')}
                  </Button>
                </ButtonGroup>
              </div>

              {this.state.display === 'list' ? (
                <>
                  <Grid container>
                    <Grid>
                      <TextInput
                        disabled={!this.state.points}
                        placeholder={langString('search')}
                        value={this.state.search}
                        onChange={(event) => {
                          const search = event.target.value;

                          this.setState((state) => ({ ...state, search }));

                          this.stackPointClick();
                        }}
                      />
                    </Grid>
                  </Grid>

                  {!this.state.points ? (
                    <Card variant="bordered">
                      <Checkbox disabled variant="radio">
                        <Text strong>
                          <Skeleton />
                        </Text>
                        <Text size="sm">
                          <Skeleton />
                        </Text>
                        <Text size="sm">
                          <Skeleton />
                        </Text>
                      </Checkbox>
                    </Card>
                  ) : (
                    <>
                      {pointsList.map((item, index) => (
                        <Card
                          key={index}
                          variant="bordered"
                          color={
                            selectedPoint?.id === item.value
                              ? 'secondary'
                              : 'basic'
                          }
                          className={classes.pointCard}
                        >
                          <Checkbox
                            variant="radio"
                            checked={selectedPoint?.id === item.value}
                            onChange={(event) =>
                              this.stackPointClick(item.stack, item.point)
                            }
                          >
                            <Grid container>
                              <Grid grow={1}>
                                <Text strong>
                                  {item.point.titleHighlight ||
                                    item.point.title}
                                </Text>
                              </Grid>
                              <Grid grow={0}>
                                <Text strong>
                                  {this.pickupTypeTitle(item.point.type)}
                                </Text>
                              </Grid>
                            </Grid>
                          </Checkbox>

                          <Grid container gap={0.4}>
                            <Icon name="location" />

                            {item.point.addressHighlight ||
                            item.point.address ? (
                              <Text size="sm">
                                (
                                {item.point.addressHighlight ||
                                  item.point.address}
                                )
                              </Text>
                            ) : null}

                            <Text size="sm">
                              ({daysFormat(item.point.days)},&nbsp;
                              {priceFormat(item.point.price)})
                            </Text>
                          </Grid>

                          {item.point.description ? (
                            <Grid container gap={0.4}>
                              <Icon name="notepadEdit" />
                              <Text size="sm">{item.point.description}</Text>
                            </Grid>
                          ) : null}

                          {Array.isArray(item.point.schedule) &&
                          item.point.schedule.find((item) => item) ? (
                            <Grid container gap={0.4}>
                              <Icon name="time" />
                              <Text size="sm">
                                {this.scheduleToString(item.point.schedule)}
                              </Text>
                            </Grid>
                          ) : null}

                          {/*<Grid container gap={0.4}>
                          <Icon name="boxSize" />
                          <Text size="sm">111</Text>
                        </Grid>*/}

                          {item.point.metro?.length ? (
                            <Grid container gap={0.4}>
                              <Icon name="metro" />

                              {item.point.metro.map((metro, index, list) => (
                                <Text key={index} size="sm">
                                  {metro.titleHighlight || metro.title}
                                  &nbsp; ({metro.line}
                                  &nbsp;-&nbsp;
                                  {metro.distance < 1
                                    ? langString('m').replace(
                                        '%',
                                        metro.distance * 1000
                                      )
                                    : langString('km').replace(
                                        '%',
                                        metro.distance
                                      )}
                                  ){index < list.length - 1 ? ',' : null}
                                </Text>
                              ))}
                            </Grid>
                          ) : null}
                        </Card>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <div
                  ref={(ref) => (this.mapContainer = ref)}
                  className={classes.mapContainer}
                >
                  <Map
                    lang={lang}
                    defaultCenter={
                      this.state.mapCenter || localityData.location
                    }
                    defaultZoom={this.state.mapCenter ? 18 : 11}
                  >
                    <Map type="clusterer">
                      {this.state.points?.map((stack) =>
                        stack[0].id !== selectedStack?.[0].id
                          ? this.renderMapStack(stack, false)
                          : null
                      )}
                    </Map>

                    {selectedStack
                      ? this.renderMapStack(selectedStack, true)
                      : null}
                  </Map>

                  {selectedStack && selectedPoint ? (
                    <Card
                      variant="bordered"
                      color="secondary"
                      className={classes.pointInfo}
                    >
                      {/*<button className="close" onClick={this.mapStackClick()}></button>*/}

                      <div>
                        <Grid container>
                          <Grid grow={0}>
                            {company.logo_path ? (
                              <Logo src={company.logo_path} />
                            ) : (
                              company.title
                            )}
                          </Grid>
                          <Grid>
                            <div>
                              {this.pickupTypeTitle(selectedPoint.type)} (
                              {tariff.title})
                            </div>
                          </Grid>
                          <Grid grow={0}>
                            <Text>{daysFormat(selectedPoint.days)}</Text>
                          </Grid>
                          <Grid grow={0}>
                            <Text>{priceFormat(selectedPoint.price)}</Text>
                          </Grid>
                        </Grid>

                        <div>{selectedPoint.address}</div>
                        <div>{selectedPoint.description}</div>
                      </div>
                    </Card>
                  ) : selectedStack ? (
                    <Card variant="bordered" className={classes.pointInfo}>
                      <div>
                        {selectedStack.map((point, index) => (
                          <Button
                            key={index}
                            variant="outlined"
                            onClick={this.mapStackClick(selectedStack, point)}
                          >
                            {point.address}
                          </Button>
                        ))}
                      </div>
                    </Card>
                  ) : null}

                  <Tooltip
                    onRender={(data) => (this.mapStackTooltipData = data)}
                  />

                  {!this.state.points ? (
                    <div className={classes.loadingBackdrop}>
                      <Loading />
                    </div>
                  ) : null}
                </div>
              )}

              <Button variant="outlined" onClick={onPrevPage}>
                {langString('delivery.method.return')}
              </Button>
            </Grid>
          </Grid>
        </Query>
      );
    }
  }
);
